import { useState, useEffect, useCallback } from "react";
import { EyeOff, Mail, Search, RotateCcw, RefreshCw, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { useAppDispatch } from "@/app/hooks";
import { endAdminImpersonation } from "@/lib/auth/endAdminImpersonation";
import { formatApiError, showWarning } from "@/lib/utils/notifications";
import { AdvancedDateRangePicker, type PresetRange } from "@/components/ui/AdvancedDateRangePicker";
import {
  fetchMessages,
  fetchMessageDetail,
  type Message,
  type MessageDetail,
} from "@/lib/api/messagesService";

function getMessageDatePresets(): PresetRange[] {
  return [
    {
      label: "Last 7 Days",
      getValue: () => {
        const today = new Date();
        return [subDays(today, 6), today];
      },
    },
    {
      label: "This Month",
      getValue: () => {
        const today = new Date();
        return [startOfMonth(today), endOfMonth(today)];
      },
    },
  ];
}

function MessageDetailView({ mailId, onBack }: { mailId: number; onBack: () => void }) {
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadDetail = async () => {
      setLoadingDetail(true);
      setError("");
      try {
        const response = await fetchMessageDetail(mailId);
        if (mounted) setDetail(response);
      } catch {
        if (mounted) setError("Unable to load message details.");
      } finally {
        if (mounted) setLoadingDetail(false);
      }
    };
    loadDetail();
    return () => {
      mounted = false;
    };
  }, [mailId]);

  return (
    <div data-testid="applicant-messages-detail-view" className="p-4 sm:p-6">
      <button
        type="button"
        data-testid="applicant-messages-detail-back-button"
        onClick={onBack}
        className="mb-4 text-sm font-medium text-blue-600 hover:text-blue-700"
      >
        Back to Messages
      </button>
      {loadingDetail ? (
        <div className="flex items-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading message details...
        </div>
      ) : error ? (
        <div className="text-red-600 text-sm">{error}</div>
      ) : detail ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">{detail.subject}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {format(new Date(detail.created_at), "MM/dd/yyyy hh:mm:ss a")}
          </p>
          <div
            className="mt-4 prose max-w-none"
            dangerouslySetInnerHTML={{ __html: detail.body_html || detail.description || "" }}
          />
        </div>
      ) : (
        <div className="text-sm text-gray-500">Message details are not available.</div>
      )}
    </div>
  );
}

export function Messages() {
  const dispatch = useAppDispatch();
  const [isAdminImpersonating, setIsAdminImpersonating] = useState(false);
  const [stopImpersonateBusy, setStopImpersonateBusy] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [pageOptions, setPageOptions] = useState<{
    current: number;
    last: number;
    total: number;
  } | null>(null);

  // Detail view state
  const [selectedMailId, setSelectedMailId] = useState<number | null>(null);

  useEffect(() => {
    const adminToken = localStorage.getItem("admin_token");
    const impersonateFlag = localStorage.getItem("impersonate");
    setIsAdminImpersonating(!!adminToken || impersonateFlag === "true");
  }, []);

  const loadMessages = useCallback(
    async (currentPage: number, search?: string, from?: Date | null, to?: Date | null) => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          perPage: 15,
          page: currentPage,
          type: "database",
        };
        const searchVal = search ?? searchText;
        if (searchVal.trim()) params.to = searchVal.trim();
        const fromDate = from !== undefined ? from : startDate;
        const toDate = to !== undefined ? to : endDate;
        if (fromDate) params.fromDt = format(fromDate, "dd-MM-yyyy");
        if (toDate) params.toDt = format(toDate, "dd-MM-yyyy");

        const response = await fetchMessages(params);

        if (Array.isArray(response)) {
          setMessages(response);
          setPageOptions(null);
        } else {
          setMessages(response.data || []);
          setPageOptions({
            current: response.current,
            last: response.last,
            total: response.total,
          });
        }
      } catch {
        setMessages([]);
        setPageOptions(null);
      } finally {
        setLoading(false);
      }
    },
    [searchText, startDate, endDate]
  );

  useEffect(() => {
    loadMessages(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadMessages(1);
  };

  const handleReset = () => {
    setSearchText("");
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    loadMessages(1, "", null, null);
  };

  const handleRefresh = () => {
    if (!loading) {
      loadMessages(page);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || (pageOptions && newPage > pageOptions.last)) return;
    setPage(newPage);
    loadMessages(newPage);
  };

  const handleStopImpersonate = async () => {
    if (stopImpersonateBusy) return;
    setStopImpersonateBusy(true);
    try {
      const originalApplicantId = localStorage.getItem("impersonated_applicant_id");
      const redirect = await endAdminImpersonation({
        dispatch,
        applicantFallback: {
          originalApplicantId,
          applicationsListPath: "/application",
        },
      });
      window.location.href = redirect;
    } catch (e) {
      showWarning(formatApiError(e));
      setStopImpersonateBusy(false);
    }
  };

  // If viewing a message detail
  if (selectedMailId !== null) {
    return (
      <div data-testid="applicant-messages-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-4 sm:p-6 pb-4 bg-gray-50 border-b border-gray-200">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">MESSAGES</h1>
          {isAdminImpersonating && (
            <button
              type="button"
              data-testid="applicant-messages-detail-stop-impersonate-button"
              onClick={() => void handleStopImpersonate()}
              disabled={stopImpersonateBusy}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors shadow-sm disabled:opacity-60"
            >
              <EyeOff className="w-4 h-4" />
              <span className="text-sm font-medium">
                {stopImpersonateBusy ? "Stopping…" : "Stop Impersonate"}
              </span>
            </button>
          )}
        </div>
        <MessageDetailView
          mailId={selectedMailId}
          onBack={() => setSelectedMailId(null)}
        />
      </div>
    );
  }

  // Pagination range (placement disambiguates top vs bottom — both render when multi-page)
  const renderPagination = (placement: "top" | "bottom") => {
    if (!pageOptions || pageOptions.last <= 1) return null;

    const { current, last } = pageOptions;
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    let startP = Math.max(1, current - Math.floor(maxVisible / 2));
    const endP = Math.min(last, startP + maxVisible - 1);
    if (endP - startP < maxVisible - 1) {
      startP = Math.max(1, endP - maxVisible + 1);
    }

    if (startP > 1) {
      pages.push(1);
      if (startP > 2) pages.push("...");
    }
    for (let i = startP; i <= endP; i++) pages.push(i);
    if (endP < last) {
      if (endP < last - 1) pages.push("...");
      pages.push(last);
    }

    return (
      <div
        data-testid={`applicant-messages-pagination-${placement}`}
        className="flex items-center gap-1"
      >
        <button
          data-testid={`applicant-messages-pagination-${placement}-prev-button`}
          onClick={() => handlePageChange(current - 1)}
          disabled={current <= 1}
          className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pages.map((p, i) =>
          typeof p === "string" ? (
            <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
          ) : (
            <button
              key={p}
              data-testid={`applicant-messages-pagination-${placement}-page-${p}`}
              onClick={() => handlePageChange(p)}
              className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${
                p === current
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          data-testid={`applicant-messages-pagination-${placement}-next-button`}
          onClick={() => handlePageChange(current + 1)}
          disabled={current >= last}
          className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div data-testid="applicant-messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-4 sm:p-6 pb-4 bg-gray-50 border-b border-gray-200">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">MESSAGES</h1>
        {isAdminImpersonating && (
          <button
            type="button"
            data-testid="applicant-messages-list-stop-impersonate-button"
            onClick={() => void handleStopImpersonate()}
            disabled={stopImpersonateBusy}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors shadow-sm disabled:opacity-60"
          >
            <EyeOff className="w-4 h-4" />
            <span className="text-sm font-medium">
              {stopImpersonateBusy ? "Stopping…" : "Stop Impersonate"}
            </span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div data-testid="applicant-messages-filters" className="p-4 sm:p-6">
        <form onSubmit={handleSearch}>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            {/* Search input */}
            <div className="w-full sm:w-64">
              <input
                data-testid="applicant-messages-search-input"
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search Message..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Date range picker */}
            <div className="w-full sm:w-72">
              <AdvancedDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start: Date | null, end: Date | null) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                placeholder="Select Date Range"
                customPresets={getMessageDatePresets()}
                showApplyButton
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                type="submit"
                data-testid="applicant-messages-search-submit-button"
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium text-sm"
              >
                <Search className="w-4 h-4" />
                Search
              </button>
              <button
                type="button"
                data-testid="applicant-messages-reset-button"
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium border border-gray-300"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                type="button"
                data-testid="applicant-messages-refresh-button"
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium border border-gray-300 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
        </form>

        {/* Page info + Pagination (top) */}
        {pageOptions && pageOptions.last > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 gap-2">
            <div className="text-sm text-gray-500">
              Page {pageOptions.current} of {pageOptions.last}
              {pageOptions.total > 0 && (
                <span className="ml-2">({pageOptions.total} Records)</span>
              )}
            </div>
            {renderPagination("top")}
          </div>
        )}

        {/* Messages list */}
        <div data-testid="applicant-messages-table-container" className="mt-4 bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No messages available.
            </div>
          ) : (
            <table data-testid="applicant-messages-table" className="w-full">
              <tbody>
                {messages.map((message, rowIndex) => (
                  <tr
                    key={message.id}
                    data-testid={`applicant-messages-row-${message.id}-${rowIndex}`}
                    onClick={() => setSelectedMailId(message.id)}
                    className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-4 w-12">
                      <Mail className="w-5 h-5 text-yellow-500" />
                    </td>
                    <td className="py-3 px-2">
                      <span className="font-semibold text-gray-800 text-sm">
                        {message.subject}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap text-sm text-gray-500">
                      {new Date(message.created_at).toLocaleString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination (bottom) */}
        {pageOptions && pageOptions.last > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 gap-2">
            <div className="text-sm text-gray-500">
              Page {pageOptions.current} of {pageOptions.last}
            </div>
            {renderPagination("bottom")}
          </div>
        )}
      </div>
    </div>
  );
}
